import cn from "classnames";
import React, { ReactNode, useEffect, useState, useCallback, useRef } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { SkeletonGrid, Table } from ".";
import { sortArray } from "../../helpers";
import { Icons } from "../shared";
import { Pagination, PaginationOptions } from "./Pagination";
import { Filter, FilterOption } from "./Filter";
import { TableScrollContext } from "./TableScrollContext";

type IBaseModel = {
    id: string | undefined;
    $type?: string | undefined;
};

export enum CardPosition {
    Primary = "primary",
    TopRight = "top-right",
    BottomRight = "bottom-right",
    Detail = "detail",
    Hidden = "hidden",
}

export type SortableTableHeaderType<T> = {
    header: string | ReactNode;
    dataKey?: string & keyof T;
    sortFunc?: (a: T, b: T) => number;
    dataClassName?: string;
    className?: string;
    width: number;
    renderFunc?: (data?: T) => ReactNode;
    sortable?: boolean;
    filterable?: boolean;
    filterType?: "text" | "select" | "date" | "daterange" | "number";
    filterOptions?: { value: string; label: string }[];
    cardPosition?: CardPosition;
};

export type ServerSideOptions<T> = {
    enabled: boolean;
    onSort?: (sortKey: string, direction: "asc" | "desc") => void;
    onFilter?: (filters: FilterOption<T>[]) => void;
    onPageChange?: (page: number, pageSize: number) => void;
    totalCount?: number;
    currentPage?: number;
    pageSize?: number;
    sortKey?: string;
    sortDirection?: "asc" | "desc";
};

export type InfiniteScrollOptions = {
    enabled: boolean;
    onLoadMore?: () => Promise<void>;
    hasMore?: boolean;
    isLoading?: boolean;
    itemHeight?: number;
    overscan?: number;
    increaseViewportBy?: number | { top: number; bottom: number };
    initialTopMostItemIndex?: number;
    followOutput?: boolean | "smooth";
    firstItemIndex?: number;//used by log tabs in firmware webconfig
    atTopStateChange?: (atTop: boolean) => void;//used by log tabs in firmware webconfig
    virtuosoRef?: React.Ref<VirtuosoHandle>; //used by log tabs in firmware webconfig
};

export type SortableTableProps<T> = {
    columns: SortableTableHeaderType<T>[];
    rowClassNames?: string;
    /** Optional per-row class names, computed from the row data (e.g. to highlight a specific row). */
    rowClassNameFunc?: (rowData: T) => string | undefined;
    data: T[] | undefined;
    className?: string;
    isLoading?: boolean;
    onRowClick?: (rowData: T) => any | void;
    onRowDoubleClick?: (rowData: T) => any | void;
    expandedRowId?: string;
    renderExpandedContent?: (rowData: T) => ReactNode;
    initialSortKey?: string & keyof T;
    initialSortDir?: "asc" | "desc";
    initialPage?: number;
    emptyValue?: React.ReactNode | string;
    pagination?: boolean;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    filtering?: boolean;
    initialFilters?: FilterOption<T>[];
    serverSide?: ServerSideOptions<T>;
    infiniteScroll?: InfiniteScrollOptions;
    mode?: "pagination" | "infiniteScroll";
    onSort?: (sortKey: string, direction: "asc" | "desc") => void;
    onFilter?: (filters: FilterOption<T>[]) => void;
    sortKey?: string;
    sortDirection?: "asc" | "desc";
    onSortChange?: (sortKey: string, direction: "asc" | "desc") => void;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
};

export type SortKeyType<T> = string & keyof T;
export type SortParams<T> = {
    sortKey?: SortKeyType<T>;
    sortFunc?: (a: T, b: T) => number;
    isAscending: boolean;
};

const compareDatesByDay = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

const parseDate = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);

    try {
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [yearStr, monthStr, dayStr] = value.split("-");
            const year = parseInt(yearStr, 10);
            const month = parseInt(monthStr, 10) - 1;
            const day = parseInt(dayStr, 10);
            return new Date(year, month, day);
        }

        const parsedDate = new Date(value);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
        }
    } catch (e) {
        console.error(e);
    }

    return null;
};

export const SortableTable = <T extends IBaseModel>(props: SortableTableProps<T>) => {
    const isServerSide = props.serverSide?.enabled ?? false;
    const isInfiniteScroll = props.mode === "infiniteScroll" && props.infiniteScroll?.enabled;
    const isPagination = props.mode === "pagination" || (!props.mode && props.pagination);
    const isDirectCallbacks = !!props.onSort;

    const [sortParams, setSortParams] = useState<SortParams<T>>({
        sortKey: props.initialSortKey ?? (props.columns.length > 0 ? props.columns[0].dataKey : undefined),
        sortFunc: props.columns.find((x) => x.dataKey === (props.initialSortKey ?? props.columns?.[0]?.dataKey))?.sortFunc,
        isAscending: props.initialSortDir !== undefined ? props.initialSortDir === "asc" : true,
    });

    const [sortedData, setSortedData] = useState<T[]>();
    const [filteredData, setFilteredData] = useState<T[]>();
    const [displayData, setDisplayData] = useState<T[]>();

    const [pagination, setPagination] = useState<PaginationOptions>({
        page: isServerSide ? props.serverSide?.currentPage ?? 1 : (props.initialPage ?? 1),
        pageSize: props.defaultPageSize || 10,
        totalCount: isServerSide ? props.serverSide?.totalCount ?? 0 : 0,
        totalPages: isServerSide ? Math.ceil((props.serverSide?.totalCount ?? 0) / (props.defaultPageSize || 10)) : 0,
    });

    const [filters, setFilters] = useState<FilterOption<T>[]>(
        props.initialFilters ||
        props.columns
            .filter((col) => col.filterable && col.dataKey)
            .map((col) => ({
                id: col.dataKey as string,
                label: typeof col.header === "string" ? col.header : String(col.dataKey),
                filterFn: (data: T, value: any) => {
                    const dataValue = data[col.dataKey as keyof T];
                    if (value === undefined || value === "") return true;

                    switch (col.filterType) {
                        case "text":
                            return String(dataValue).toLowerCase().includes(String(value).toLowerCase());
                        case "number":
                            return Number(dataValue) === Number(value);
                        case "date":
                            const filterDate = parseDate(value);
                            const dataDate = parseDate(dataValue);

                            if (filterDate && dataDate) {
                                return compareDatesByDay(filterDate, dataDate);
                            }

                            return String(dataValue).includes(value);
                        case "daterange":
                            const dataDate2 = parseDate(dataValue);
                            if (!dataDate2) return false;

                            const fromDate = value.from ? parseDate(value.from) : null;
                            const toDate = value.to ? parseDate(value.to) : null;

                            if (fromDate && dataDate2 < fromDate) return false;
                            if (toDate && dataDate2 > toDate) return false;

                            return true;
                        case "select":
                            return String(dataValue) === String(value);
                        default:
                            return String(dataValue).toLowerCase().includes(String(value).toLowerCase());
                    }
                },
                type: col.filterType || "text",
                options: col.filterOptions,
            }))
    );

    useEffect(() => {
        if (isServerSide && isPagination) {
            setPagination((prev) => ({
                ...prev,
                page: props.serverSide?.currentPage ?? 1,
                totalCount: props.serverSide?.totalCount ?? 0,
                totalPages: Math.ceil((props.serverSide?.totalCount ?? 0) / prev.pageSize),
                ...(props.serverSide?.pageSize && props.serverSide.pageSize !== prev.pageSize ? { pageSize: props.serverSide.pageSize } : {}),
            }));
        }
    }, [props.serverSide?.currentPage, props.serverSide?.totalCount, props.serverSide?.pageSize, isServerSide, isPagination]);

    useEffect(() => {
        if (isServerSide || isInfiniteScroll || isDirectCallbacks) {
            setSortedData([...(props.data ?? [])]);
            return;
        }

        const sortKey = sortParams.sortKey ?? (props.columns.length > 0 ? props.columns[0].dataKey : undefined);
        const sortFunc = sortParams.sortFunc ?? props.columns.find((x) => x.dataKey === sortKey)?.sortFunc;

        if (sortKey === undefined) {
            console.warn("Sort key is undefined for a sortable table...");
            setSortedData([...(props.data ?? [])]);
            return;
        }

        setSortedData([...(sortArray(props.data, sortFunc, sortKey, sortParams.isAscending ? "asc" : "desc") ?? [])]);
    }, [sortParams, props.data, props.columns, isServerSide, isInfiniteScroll, isDirectCallbacks]);

    // Handle filtering
    useEffect(() => {
        if (isServerSide || isInfiniteScroll || isDirectCallbacks) {
            setFilteredData(sortedData ?? []);
            return;
        }

        if (!sortedData) return;

        const activeFilters = filters.filter((f) => f.value !== undefined && f.value !== "");
        if (activeFilters.length === 0) {
            setFilteredData(sortedData);
            return;
        }

        const filtered = sortedData.filter((item) => activeFilters.every((filter) => filter.filterFn(item, filter.value)));
        setFilteredData(filtered);
    }, [filters, sortedData, isServerSide, isInfiniteScroll, isDirectCallbacks]);

    useEffect(() => {
        if (isServerSide || isInfiniteScroll || isDirectCallbacks) {
            setDisplayData(props.data ?? []);
            return;
        }

        if (!filteredData) return;

        setPagination((prev) => ({
            ...prev,
            totalCount: filteredData.length,
            totalPages: Math.ceil(filteredData.length / prev.pageSize),
        }));

        if (isPagination) {
            const startIndex = (pagination.page - 1) * pagination.pageSize;
            const endIndex = startIndex + pagination.pageSize;
            setDisplayData(filteredData.slice(startIndex, endIndex));
        } else {
            setDisplayData(filteredData);
        }
    }, [filteredData, pagination.page, pagination.pageSize, isPagination, isServerSide, isInfiniteScroll, isDirectCallbacks, props.data]);

    const handleHeaderClick = (e: React.MouseEvent<HTMLDivElement>, key?: SortKeyType<T>) => {
        if (!key) return;

        e.preventDefault();
        e.stopPropagation();

        if (isServerSide) {
            const currentSortKey = props.serverSide?.sortKey;
            const currentDirection = props.serverSide?.sortDirection ?? "desc";
            const newDirection = currentSortKey !== key ? "desc" : currentDirection === "asc" ? "desc" : "asc";

            props.serverSide?.onSort?.(key as string, newDirection);
        } else if (isDirectCallbacks) {
            const currentSortKey = props.sortKey;
            const currentDirection = props.sortDirection ?? "desc";
            const newDirection = currentSortKey !== key ? "desc" : currentDirection === "asc" ? "desc" : "asc";

            props.onSort?.(key as string, newDirection);
        } else {
            setSortParams((prev) => {
                document.dispatchEvent(new Event("dropdown:force"));
                const newIsAscending = prev.sortKey !== key ? false : !prev.isAscending;
                props.onSortChange?.(key as string, newIsAscending ? "asc" : "desc");
                return {
                    sortKey: key,
                    sortFunc: props.columns.find((x) => x.dataKey === key)?.sortFunc,
                    isAscending: newIsAscending,
                };
            });
        }
    };

    const handlePageChange = (page: number) => {
        if (isServerSide) {
            props.serverSide?.onPageChange?.(page, pagination.pageSize);
        } else {
            setPagination((prev) => ({ ...prev, page }));
            props.onPageChange?.(page);
        }
    };

    const handlePageSizeChange = (pageSize: number) => {
        if (isServerSide) {
            setPagination((prev) => ({ ...prev, pageSize, page: 1 }));
            props.serverSide?.onPageChange?.(1, pageSize);
        } else {
            setPagination((prev) => ({ ...prev, pageSize, page: 1 }));
            props.onPageSizeChange?.(pageSize);
        }
    };

    const handleFilterChange = (newFilters: FilterOption<T>[]) => {
        setFilters(newFilters);

        if (isServerSide) {
            props.serverSide?.onFilter?.(newFilters);
        } else if (isDirectCallbacks) {
            props.onFilter?.(newFilters);
        } else if (!isInfiniteScroll) {
            setPagination((prev) => ({ ...prev, page: 1 }));
        }
    };

    const getCurrentSortKey = () => {
        if (isServerSide) {
            return props.serverSide?.sortKey;
        } else if (isDirectCallbacks) {
            return props.sortKey;
        }
        return sortParams.sortKey;
    };

    const getCurrentSortDirection = () => {
        if (isServerSide) {
            return props.serverSide?.sortDirection ?? "desc";
        } else if (isDirectCallbacks) {
            return props.sortDirection ?? "desc";
        }
        return sortParams.isAscending ? "asc" : "desc";
    };

    const handleLoadMore = useCallback(async () => {
        if (props.infiniteScroll?.onLoadMore && !props.infiniteScroll.isLoading) {
            await props.infiniteScroll.onLoadMore();
        }
    }, [props.infiniteScroll]);

    const virtuosoWrapperRef = useRef<HTMLDivElement>(null);
    const [virtuosoHeight, setVirtuosoHeight] = useState(400);
    const [isScrolling, setIsScrolling] = useState(false);

    useEffect(() => {
        if (!isInfiniteScroll) return;

        const el = virtuosoWrapperRef.current;
        if (!el) return;

        const measure = () => {
            const rect = el.getBoundingClientRect();
            const available = window.innerHeight - rect.top - 16;
            setVirtuosoHeight(Math.max(300, available));
        };

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        window.addEventListener("resize", measure);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [isInfiniteScroll]);

    const headerDivs = props.columns.map((c, i) => {
        const currentSortKey = getCurrentSortKey();
        const currentDirection = getCurrentSortDirection();
        const isCurrentSort = c.dataKey === currentSortKey;
        const isAscending = currentDirection === "asc";

        const showSortArrows = c.dataKey && c.sortable !== false;
        return (
            <Table.HeaderCol key={`hc-${i}`} className={cn(c.className, "text-left pb-3 pr-3 uppercase font-semibold min-w-0", { "cursor-pointer": showSortArrows })} style={{ width: c.width, flexGrow: c.width }} onClick={(e) => showSortArrows ? handleHeaderClick(e, c.dataKey) : undefined} onMouseDown={(e) => e.preventDefault()}>
                {c.header}
                {showSortArrows && (isCurrentSort ? isAscending ? <Icons.SortUp className="inline text-brand-primary" size={"1em"} /> : <Icons.SortDown className="inline text-brand-primary" size={"1em"} /> : <Icons.UnSorted className="inline text-muted-body-text hover:text-brand-secondary" />)}
            </Table.HeaderCol>
        );
    });

    const renderTableRow = (data: T, index: number) => {
        const isExpanded = props.expandedRowId !== undefined && data.id === props.expandedRowId;
        return (
            <React.Fragment key={data.id || index}>
                <Table.Row
                    className={cn(props.rowClassNames, props.rowClassNameFunc?.(data), "flex items-center p-1 px-2", {
                        "hover:bg-background-page-highlight-gray cursor-pointer": props.onRowClick,
                    })}
                    onClick={() => props.onRowClick && props.onRowClick(data)}
                    onDoubleClick={() => props.onRowDoubleClick && props.onRowDoubleClick(data)}
                >
                    {props.columns.map((c, i) => (
                        // min-w-0 matches the virtuoso row path: without it a flex cell refuses to
                        // shrink below its content's intrinsic width and the whole page scrolls
                        // horizontally instead of the content wrapping.
                        <Table.Col key={`${data.id}-${i}`} className={cn(c.dataClassName, "min-w-0")} style={{ width: c.width, flexGrow: c.width }}>
                            <>{c.renderFunc ? c.renderFunc(data) : c.dataKey && data[c.dataKey]}</>
                        </Table.Col>
                    ))}
                </Table.Row>
                {isExpanded && props.renderExpandedContent && (
                    <Table.Row className="border-b border-separators-page">
                        <Table.Col>
                            {props.renderExpandedContent(data)}
                        </Table.Col>
                    </Table.Row>
                )}
            </React.Fragment>
        );
    };

    const renderCardRow = (data: T, index: number) => {
        const columnsWithData = props.columns
            .map((c, i) => {
                const value = c.renderFunc ? c.renderFunc(data) : c.dataKey ? (data[c.dataKey] as ReactNode) : null;
                const hasHeader = typeof c.header === "string" && c.header.trim() !== "";

                let position = c.cardPosition;
                if (!position) {
                    if (i === 0 && hasHeader) {
                        position = CardPosition.Primary;
                    } else if (!hasHeader) {
                        position = CardPosition.BottomRight;
                    } else {
                        position = CardPosition.Detail;
                    }
                }

                return {
                    column: c,
                    index: i,
                    value,
                    hasHeader,
                    position,
                };
            })
            .filter((item) => item.value || item.value === 0);

        const primary = columnsWithData.find((item) => item.position === CardPosition.Primary);
        const topRight = columnsWithData.filter((item) => item.position === CardPosition.TopRight);
        const bottomRight = columnsWithData.filter((item) => item.position === CardPosition.BottomRight);
        const details = columnsWithData.filter((item) => item.position === CardPosition.Detail);

        return (
            <div
                key={data.id || index}
                className={cn("bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm relative", {
                    "hover:shadow-md cursor-pointer active:shadow-lg transition-shadow": props.onRowClick,
                })}
                onClick={() => props.onRowClick && props.onRowClick(data)}
            >
                <div className="flex justify-between items-start gap-3 mb-3">
                    {primary && (
                        <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-500 mb-1">{primary.column.header}</div>
                            <div className={cn("text-base font-semibold text-gray-900 break-words", primary.column.dataClassName)}>{primary.value}</div>
                        </div>
                    )}

                    {topRight.length > 0 && (
                        <div className="flex gap-2 items-start flex-shrink-0 [&_*]:!my-0 [&_*]:!mx-0">
                            {topRight.map((item) => (
                                <div key={`${data.id}-${item.index}`} className={cn("text-sm", item.column.dataClassName)}>
                                    {item.value}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {details.length > 0 && (
                    <div className="space-y-2 mb-3">
                        {details.map((item) => (
                            <div key={`${data.id}-${item.index}`} className="flex items-start gap-2">
                                <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{item.column.header}:</span>
                                {/* min-w-0 so long unbreakable tokens (ids, type names) actually
                                    break instead of setting the card's minimum width. */}
                                <span className={cn("text-sm text-gray-900 flex-1 min-w-0 break-words", item.column.dataClassName)}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {bottomRight.length > 0 && (
                    <div className="flex gap-2 justify-end items-center pt-2 border-t border-gray-100">
                        {bottomRight.map((item) => (
                            <div key={`${data.id}-${item.index}`} className={cn("text-sm", item.column.dataClassName)}>
                                {item.value}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderVirtuosoRow = (index: number, data: T) => {
        if (!data) {
            return <div style={{ height: "48px" }}>Loading...</div>;
        }

        const isExpanded = props.expandedRowId !== undefined && data.id === props.expandedRowId;

        return (
            <div
                key={data.id || index}
                className={cn("border-b border-separators-page", {
                    "hover:bg-background-page-highlight-gray cursor-pointer": props.onRowClick,
                })}
                onClick={() => props.onRowClick && props.onRowClick(data)}
                onDoubleClick={() => props.onRowDoubleClick && props.onRowDoubleClick(data)}
            >
                <div className={cn(props.rowClassNames, props.rowClassNameFunc?.(data), "flex items-center p-2 px-2 overflow-hidden")}>
                    {props.columns.map((c, i) => (
                        <div key={`${data.id || index}-${i}`} className={cn(c.dataClassName, "py-1 min-w-0")} style={{ width: c.width, flexGrow: c.width }}>
                            <>{c.renderFunc ? c.renderFunc(data) : c.dataKey && data[c.dataKey]}</>
                        </div>
                    ))}
                </div>
                {isExpanded && props.renderExpandedContent && (
                    <div className="px-4 pb-3">
                        {props.renderExpandedContent(data)}
                    </div>
                )}
            </div>
        );
    };

    if (isInfiniteScroll) {
        return (
            <div className={cn(props.className, "flex flex-col")}>
                {props.filtering && filters.length > 0 && <Filter filters={filters} onFilterChange={handleFilterChange} />}

                {/* Desktop Table View - Hidden on screens < 600px */}
                <div className="hidden sm:flex sm:flex-col">
                    <Table.Container>
                        <Table.Header>
                            <Table.HeaderRow className="flex">{headerDivs}</Table.HeaderRow>
                        </Table.Header>
                    </Table.Container>

                    <div ref={virtuosoWrapperRef} className="border border-t-0" style={{ height: virtuosoHeight }}>
                        {props.isLoading && displayData?.length === 0 ? (
                            <div className="p-4">
                                <SkeletonGrid cols={props.columns.length} rows={3} />
                            </div>
                        ) : displayData && displayData.length === 0 && props.emptyValue ? (
                            <div className="text-center p-8">{props.emptyValue}</div>
                        ) : (
                          <TableScrollContext.Provider value={isScrolling}>
                            <Virtuoso
                                ref={props.infiniteScroll?.virtuosoRef}
                                totalCount={displayData?.length || 0}
                                fixedItemHeight={props.infiniteScroll?.itemHeight}
                                isScrolling={setIsScrolling}
                                increaseViewportBy={props.infiniteScroll?.increaseViewportBy ?? 0}
                                followOutput={props.infiniteScroll?.followOutput}
                                {...(props.infiniteScroll?.firstItemIndex !== undefined
                                    ? { firstItemIndex: props.infiniteScroll.firstItemIndex }
                                    : {})}
                                atTopStateChange={props.infiniteScroll?.atTopStateChange}
                                endReached={() => {
                                    if (props.infiniteScroll?.hasMore && !props.infiniteScroll?.isLoading) {
                                        handleLoadMore();
                                    }
                                }}
                                overscan={5}
                                itemContent={(index) => {
                                    const dataIndex = index - (props.infiniteScroll?.firstItemIndex ?? 0);
                                    const data = displayData?.[dataIndex];
                                    if (!data) {
                                        return <div style={{ height: "48px", padding: "8px" }}>Loading item {dataIndex}...</div>;
                                    }
                                    return renderVirtuosoRow(dataIndex, data);
                                }}
                                atBottomThreshold={100}
                                components={{
                                    Footer: () => {
                                        if (props.infiniteScroll?.isLoading) {
                                            return (
                                                <div className="p-4 text-center">
                                                    <Icons.Spinner className="animate-spin inline mr-2" />
                                                    Loading more...
                                                </div>
                                            );
                                        }

                                        if (!props.infiniteScroll?.hasMore && displayData && displayData.length > 0) {
                                            return <div className="p-4 text-center text-gray-500">No more items to load</div>;
                                        }

                                        return null;
                                    },
                                }}
                            />
                          </TableScrollContext.Provider>
                        )}
                    </div>
                </div>

                <div
                    className="block sm:hidden flex-1 min-h-0 overflow-y-auto"
                    onScroll={(e) => {
                        const target = e.target as HTMLDivElement;
                        const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
                        if (bottom && props.infiniteScroll?.hasMore && !props.infiniteScroll?.isLoading) {
                            handleLoadMore();
                        }
                    }}
                >
                    {props.isLoading && displayData?.length === 0 ? (
                        <div className="p-4">
                            <SkeletonGrid cols={1} rows={3} />
                        </div>
                    ) : displayData && displayData.length === 0 && props.emptyValue ? (
                        <div className="text-center p-8 text-gray-500">{props.emptyValue}</div>
                    ) : (
                        <>
                            {displayData?.map((data, index) => renderCardRow(data, index))}
                            {props.infiniteScroll?.isLoading && (
                                <div className="p-4 text-center">
                                    <Icons.Spinner className="animate-spin inline mr-2" />
                                    Loading more...
                                </div>
                            )}
                            {!props.infiniteScroll?.hasMore && displayData && displayData.length > 0 && <div className="p-4 text-center text-gray-500">No more items to load</div>}
                        </>
                    )}
                </div>
            </div>
        );
    }

    const dataDivs = displayData?.map((data, r) => renderTableRow(data, r));
    const cardDivs = displayData?.map((data, r) => renderCardRow(data, r));

    return (
        <div className={props.className}>
            {props.filtering && filters.length > 0 && <Filter filters={filters} onFilterChange={handleFilterChange} />}

            <div className="hidden sm:block">
                <Table.Container>
                    <Table.Header>
                        <Table.HeaderRow className="flex">{headerDivs}</Table.HeaderRow>
                    </Table.Header>
                    <Table.Body>
                        {props.isLoading && (
                            <Table.Row>
                                <Table.Col>
                                    <SkeletonGrid cols={props.columns.length} rows={3} />
                                </Table.Col>
                            </Table.Row>
                        )}
                        {displayData && displayData.length === 0 && props.emptyValue && !props.isLoading && (
                            <Table.Row>
                                <Table.Col className="text-center">{props.emptyValue}</Table.Col>
                            </Table.Row>
                        )}
                        {!props.isLoading && displayData && dataDivs}
                    </Table.Body>
                </Table.Container>
            </div>

            <div className="block sm:hidden">
                {props.isLoading && (
                    <div className="p-4">
                        <SkeletonGrid cols={1} rows={3} />
                    </div>
                )}
                {displayData && displayData.length === 0 && props.emptyValue && !props.isLoading && <div className="text-center p-8 text-gray-500">{props.emptyValue}</div>}
                {!props.isLoading && displayData && cardDivs}
            </div>

            {isPagination && <Pagination currentPage={pagination.page} pageSize={pagination.pageSize} totalCount={pagination.totalCount} onPageChange={handlePageChange} onPageSizeChange={props.pageSizeOptions ? handlePageSizeChange : undefined} pageSizeOptions={props.pageSizeOptions} />}
        </div>
    );
};
